import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function SubscriptionManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [limits, setLimits] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/subscription`);
      setSubscription(response.data.data.subscription);
      setLimits(response.data.data.limits);
      setUsage(response.data.data.usage);
      setLoading(false);
    } catch (error) {
      console.error('Load subscription error:', error);
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will be downgraded to the Free plan at the end of your billing period.')) {
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/billing/subscription/cancel`);
      alert('Subscription canceled. You will have access until the end of your billing period.');
      loadSubscription();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Failed to cancel'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageStripe = async () => {
    setActionLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/stripe/portal`);
      window.location.href = response.data.data.url;
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Failed to open portal'));
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '100px 20px' }}>
        <h2>Loading subscription details...</h2>
      </div>
    );
  }

  const getPlanBadgeColor = (planId) => {
    const colors = {
      FREE: '#6c757d',
      STARTER: '#17a2b8',
      PROFESSIONAL: '#28a745',
      AGENCY: '#007bff'
    };
    return colors[planId] || '#6c757d';
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min((used / limit) * 100, 100);
  };

  return (
    <div className="container" style={{ maxWidth: '1000px', padding: '40px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <h1>Subscription & Usage</h1>
        <button onClick={() => navigate('/dashboard')} style={{ background: '#6c757d' }}>
          ← Back to Dashboard
        </button>
      </div>

      {/* Current Plan Card */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h2 style={{ margin: 0 }}>Current Plan</h2>
              <span style={{
                padding: '4px 16px',
                background: getPlanBadgeColor(limits?.planId),
                color: 'white',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                {limits?.planName || 'Free'}
              </span>
            </div>
            {subscription && (
              <p style={{ color: '#666', marginBottom: '8px' }}>
                Status: <strong style={{
                  color: subscription.status === 'active' ? '#28a745' : '#dc3545'
                }}>
                  {subscription.status}
                </strong>
              </p>
            )}
            {subscription?.current_period_end && (
              <p style={{ color: '#666' }}>
                Renews: {new Date(subscription.current_period_end).toLocaleDateString()}
              </p>
            )}
            {subscription?.cancel_at_period_end && (
              <p style={{ color: '#dc3545', fontWeight: 'bold', marginTop: '8px' }}>
                ⚠️ Will be canceled at end of billing period
              </p>
            )}
          </div>

          <div style={{ textAlign: 'right' }}>
            {limits?.planId !== 'FREE' && (
              <button
                onClick={() => navigate('/pricing')}
                style={{ marginBottom: '8px' }}
              >
                Upgrade Plan
              </button>
            )}
            {limits?.planId === 'FREE' && (
              <button
                onClick={() => navigate('/pricing')}
                style={{ background: '#28a745' }}
              >
                Upgrade Now
              </button>
            )}
          </div>
        </div>

        {/* Plan Limits */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          padding: '16px',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Raffles per Month</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
              {limits?.rafflesPerMonth === -1 ? 'Unlimited' : limits?.rafflesPerMonth}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Max Entries per Raffle</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
              {limits?.maxEntriesPerRaffle === -1 ? 'Unlimited' : limits?.maxEntriesPerRaffle.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Usage Card */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '20px' }}>Current Month Usage</h3>

        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span>Raffles Created</span>
            <span style={{ fontWeight: 'bold' }}>
              {usage?.raffles_created || 0}
              {limits?.rafflesPerMonth !== -1 && ` / ${limits?.rafflesPerMonth}`}
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '12px',
            background: '#e0e0e0',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${getUsagePercentage(usage?.raffles_created || 0, limits?.rafflesPerMonth)}%`,
              height: '100%',
              background: getUsagePercentage(usage?.raffles_created || 0, limits?.rafflesPerMonth) > 80 ? '#dc3545' : '#28a745',
              transition: 'width 0.3s'
            }} />
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span>Total Entries Processed</span>
            <span style={{ fontWeight: 'bold' }}>{usage?.total_entries?.toLocaleString() || 0}</span>
          </div>
          <div style={{
            width: '100%',
            height: '12px',
            background: '#e0e0e0',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              background: '#007bff'
            }} />
          </div>
        </div>

        {limits?.rafflesPerMonth !== -1 && usage?.raffles_created >= limits?.rafflesPerMonth && (
          <div style={{
            marginTop: '20px',
            padding: '16px',
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            color: '#856404'
          }}>
            <strong>⚠️ Monthly Limit Reached</strong>
            <p style={{ margin: '8px 0 0 0' }}>
              You've reached your monthly raffle limit. Upgrade your plan to create more raffles.
            </p>
          </div>
        )}
      </div>

      {/* Billing Management */}
      {subscription && subscription.billing_provider !== 'none' && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <h3 style={{ marginBottom: '20px' }}>Billing Management</h3>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {subscription.billing_provider === 'stripe' && (
              <button
                onClick={handleManageStripe}
                disabled={actionLoading}
                style={{ flex: '1 1 200px' }}
              >
                {actionLoading ? 'Loading...' : '💳 Manage Billing (Stripe)'}
              </button>
            )}

            {subscription.billing_provider === 'paypal' && (
              <a
                href="https://www.paypal.com/myaccount/autopay/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ flex: '1 1 200px', textDecoration: 'none' }}
              >
                <button style={{ width: '100%', background: '#0070ba' }}>
                  Manage PayPal Subscription
                </button>
              </a>
            )}

            {!subscription.cancel_at_period_end && (
              <button
                onClick={handleCancelSubscription}
                disabled={actionLoading}
                style={{ flex: '1 1 200px', background: '#dc3545' }}
              >
                {actionLoading ? 'Processing...' : 'Cancel Subscription'}
              </button>
            )}
          </div>

          <p style={{ fontSize: '14px', color: '#666', marginTop: '16px' }}>
            {subscription.billing_provider === 'stripe' && 'Manage your payment methods, billing history, and invoices through Stripe.'}
            {subscription.billing_provider === 'paypal' && 'Manage your PayPal subscription directly through PayPal.'}
          </p>
        </div>
      )}

      {/* Upgrade CTA */}
      {limits?.planId === 'FREE' && (
        <div className="card" style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center'
        }}>
          <h3 style={{ marginBottom: '12px' }}>Unlock More Features</h3>
          <p style={{ marginBottom: '20px', opacity: 0.9 }}>
            Upgrade to a paid plan and get unlimited raffles, advanced analytics, and priority support.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            style={{
              background: 'white',
              color: '#667eea',
              fontWeight: 'bold'
            }}
          >
            View Plans & Pricing
          </button>
        </div>
      )}
    </div>
  );
}

export default SubscriptionManagement;
